import React, { useLayoutEffect, useRef, useState } from 'react';
import cx from 'classnames';
import { Collapse } from 'react-bootstrap';
import { Helmet } from 'react-helmet';
import { Link, useParams } from 'react-router-dom';

import { paths } from 'config/path_helpers';

import { useQueries } from '@schools/app/queries';
import CategoryProfileList from '@schools/app/components/category_profile_list';
import CategoryProfileListTrigger from '@schools/app/components/category_profile_list/trigger';
import Contents from '@schools/app/components/contents';
import CTA from '@schools/app/components/cta';
import PageOverlay from '@schools/app/components/page_overlay';
import ApplicationDeadlineAccordian from '@schools/app/organisms/application_deadlines_accordian';
import Footer from '@schools/app/organisms/footer';
import ProfileHeroAndSectionNav from '@schools/app/organisms/profile_hero_and_section_nav';

import MagooshLogo from '@magoosh/assets/magoosh-logo-purple.png';
import sharedStyle from '../style.module.scss';

interface Props {
  preview?: boolean;
}

const Profile: React.FC<Props> = (props) => {
  let { categorySlug, slug } = useParams();
  const q = useQueries();
  const fixedNavbarRef = useRef(null);
  const [fixedNavbarHeight, setFixedNavbarHeight] = useState<number>(0);
  const [profileSelectIsOpen, setProfileSelectIsOpen] = useState<boolean>(false);

  const sectionNavHeight = 54;
  const stickyHeroBarHeight = 96;

  const schoolProfileQuery = q.schoolProfileQuery(slug, categorySlug, !!props.preview);
  const profile = schoolProfileQuery.data;

  categorySlug = categorySlug || profile?.defaultCategorySlug;
  const schoolProfileCategoryQuery = q.schoolProfileCategoryQuery(categorySlug, !!props.preview, { enabled: !!categorySlug });
  const category = schoolProfileCategoryQuery.data;

  useLayoutEffect(() => {
    toggleProfileSelect(false);
  }, [category, slug]);

  useLayoutEffect(() => {
    setFixedNavbarHeight(fixedNavbarRef?.current?.offsetHeight || 0);
  }, []);

  const toggleProfileSelect = (value) => {
    if (value) {
      document.body.classList.add(sharedStyle.overflowHidden);
    } else {
      document.body.classList.remove(sharedStyle.overflowHidden);
    }

    setProfileSelectIsOpen(value);
  };

  return (
    <div className="d-flex flex-column" style={{ minHeight: '100vh' }}>
      {
        profile && (
          <Helmet>
            {profile.seoMetaDescription && <meta name="description" content={profile.seoMetaDescription} />}
            <title>{profile.name} - Overview & Rankings</title>
          </Helmet>
        )
      }

      <div className={cx(sharedStyle.fixedNavbar, { [sharedStyle.in]: profileSelectIsOpen })} ref={fixedNavbarRef}>
        <div className="container align-items-center d-flex u-padding-V-m">
          <div>
            <a href="https://magoosh.com">
              <img alt="Magoosh" className={sharedStyle.logo} src={MagooshLogo} />
            </a>
          </div>

          <div className="flex-1">
            <div className="d-flex align-items-center justify-content-end" style={{ gap: 20 }}>
              {
                category && (
                  <div className="hidden-xs">
                    <Link
                      className="small text-body text-no-decoration text-underdash"
                      to={paths.admissions.schoolProfileCategory(category.slug, props.preview)}
                    >
                      {category.name}
                    </Link>
                  </div>
                )
              }

              <CategoryProfileListTrigger
                currentProfile={profile}
                isOpen={profileSelectIsOpen}
                onTrigger={() => toggleProfileSelect(!profileSelectIsOpen)}
              />
            </div>
          </div>
        </div>

        <Collapse in={profileSelectIsOpen}>
          <div className={sharedStyle.categoryProfileListOverlay}>
            <CategoryProfileList
              category={category}
              className="u-padding-B-l"
              preview={props.preview}
            />
          </div>
        </Collapse>
      </div>

      <div className="flex-1" style={{ position: 'relative' }}>
        {<PageOverlay in={profileSelectIsOpen} onClick={() => toggleProfileSelect(false)} />}

        <ProfileHeroAndSectionNav
          offsetY={fixedNavbarHeight}
          profile={profile}
          sectionNavHeight={sectionNavHeight}
          stickyHeroBarHeight={stickyHeroBarHeight}
        />

        <div className="container u-margin-T-l">
          <div className={cx(sharedStyle.contents, 'd-flex')}>
            <div className={cx(sharedStyle.contentsSections, 'd-flex flex-column flex-1')}>
              <Contents contents={profile?.contents} />
            </div>

            <div className={sharedStyle.contentsSidebar}>
              {
                !!profile?.applicationDeadlines?.length && (
                  <div className="u-margin-B-l">
                    {/*Adding a key ensures this components works as user navigates from profile to
                    profile (was not highlighted the current deadline when navigating back)*/}
                    <ApplicationDeadlineAccordian deadlines={profile?.applicationDeadlines} key={profile.contentfulEntryId} />
                  </div>
                )
              }

              {
                profile?.cta && (
                  <CTA
                    {...profile.cta}
                    mobileOffsetY={fixedNavbarHeight}
                    offsetY={fixedNavbarHeight + stickyHeroBarHeight + sectionNavHeight}
                  />
                )
              }
            </div>
          </div>
        </div>

        <div className="u-margin-T-xl">
          <Footer />
        </div>
      </div>
    </div>
  );
};

export default Profile;
